import React, { useEffect, useRef, useState, useCallback } from "react";
import { IoArrowBack, IoClose } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import Spinner from "react-bootstrap/Spinner";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import "bootstrap/dist/css/bootstrap.min.css";
import styles from "./chatbox.module.css";


import BlockModal from "./BlockModel";
import ReportModal from "./ReportModel";
import UnmatchModal from "./UnMatchModel";
import Loader from "../../../Utility/Loader";

import log from "../../../../utils/log";
import { useCookies } from "../../../../hooks/useCookies";
import * as messageAction from "../../../../actions/Message";
import * as profileAction from "../../../../actions/Profile";
import { unblockUser, getBlockedList, getReportedList } from "../../../../actions/match";

import { useSocket } from "../../../../Context/SockerContext";
import { UserApprovalEnum } from "../../../../utils/constants";
import { getProfileImageUrl, resolveProfileId } from "../../../../utils/serverUtils";

// Utility
const formatDate = (date) => {
  const today = dayjs();
  const messageDate = dayjs(date);
  const diff = today.diff(messageDate, "day");
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return messageDate.format("DD/MM/YYYY");
};

export default function ChatBox({ className = "" }) {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { getCookie } = useCookies();

  const userId = getCookie("userId");
  const chatContainerRef = useRef(null);
  const chatSectionRef = useRef(null); // Ref for the main section

  const [loader, setLoader] = useState(false);
  const [text, setText] = useState("");
  const [isSending, setIsSending] = useState(false);

  const [messages, setMessages] = useState({});
  const [conversationId, setConversationId] = useState(
    location.state?.conversationId || sessionStorage.getItem("conversationId") || null
  );

  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnmatchModal, setShowUnmatchModal] = useState(false);

  const [participantProfile, setParticipantProfile] = useState({
    name: "",
    image: "",
    active: 1,
    approval: 10,
    deletedAt: "",
  });

  const [chatAction, setChatAction] = useState({
    isMutualMatch: true,
    isUnmatched: false,
    isBlocked: false,
    blockedByMe: false,
    blockedByMe: false,
    isReported: false,
    reportedByMe: false,
  });
  const [mobile, setMobile] = useState(false);
  const [isReportBannerVisible, setIsReportBannerVisible] = useState(true);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setMobile(true);
    }
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setMobile(true);
      } else {
        setMobile(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Visual Viewport handling for mobile keyboard - REMOVED for reversion
  /* 
  useEffect(() => {
    // ... logic removed ...
  }, []); 
  */

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoader(true);
      const data = await profileAction.fetchDataById(id, token);
      console.log("ChatBox fetchUserDetails data:", data);
      if (data) {
        setParticipantProfile({
          name: data.first_name || "",
          image: getProfileImageUrl(data),
          active: data.active !== undefined ? data.active : 1,
          approval: data.approval !== undefined ? data.approval : UserApprovalEnum.APPROVED,
          deletedAt: data.deleted_at,
          profile_token: data.profile_token || token,
        });
      }

      const userStatus = await profileAction.getUserActionStatus(id);
      if (userStatus.status) {
        let blockedByMe = false;



        if (userStatus.data.isBlocked) {
          try {
            const blockedList = await getBlockedList();
            // check if 'id' is in the blockedList
            // blockedList might be array of objects with user_id or id
            if (Array.isArray(blockedList)) {
               blockedByMe = blockedList.some(u => 
                 (u.user_id && String(u.user_id) === String(id)) || 
                 (u.id && String(u.id) === String(id))
               );
            }
          } catch (err) {
            console.error("Error fetching blocked list", err);
          }
        }



        // Determine if I reported this user
        let reportedByMe = false;
        
        if (userStatus.data.isReported) {
          try {
            const reportedList = await getReportedList();
             console.log("Debug reportedList for ID:", id, reportedList);
             
             // Handle if response is wrapped in { data: [...] }
             let actualList = reportedList;
             if (!Array.isArray(reportedList) && reportedList?.data && Array.isArray(reportedList.data)) {
               actualList = reportedList.data;
             }

             if (Array.isArray(actualList)) {
               // Check if ANY item matches the target user ID
               const listMatch = actualList.some(item => {
                  const targetId = String(id);
                  // Strict check for user_id as requested, but keep robust fallbacks
                  if (typeof item === 'object' && item !== null) {
                    return (
                      (item.user_id && String(item.user_id) === targetId) || // Primary check per user
                      (item.id && String(item.id) === targetId) ||
                      (item.reported_user_id && String(item.reported_user_id) === targetId) ||
                      (item.reported_id && String(item.reported_id) === targetId) || 
                      (item.reportee_id && String(item.reportee_id) === targetId) ||
                      (item.receiver_id && String(item.receiver_id) === targetId) ||
                      (item.user && item.user.id && String(item.user.id) === targetId)
                    );
                  }
                  // primitive check
                  return String(item) === targetId;
               });
               if (listMatch) reportedByMe = true;
            }
          } catch (err) {
            console.error("Error fetching reported list", err);
          }
        }

        setChatAction({
          isMutualMatch: userStatus.data.isMutualMatch,
          isUnmatched: userStatus.data.isUnmatched,
          isBlocked: userStatus.data.isBlocked,
          blockedByMe: blockedByMe,
          isReported: userStatus.data.isReported,
          reportedByMe: reportedByMe, 
        });
      }
    } catch (error) {
      log.error("Error fetching user details:", error);
    } finally {
      setLoader(false);
    }
  }, [id, token]);

  const fetchConversation = useCallback(async () => {
    try {
      const res = await messageAction.getChatRoom(id, token);
      console.log("getChatRoom response:", res);
      const convId = res?.conversationId || res?.conversation_id || res?.data?.conversationId;
      if (convId) {
        setConversationId(convId);
        sessionStorage.setItem("conversationId", convId);
      }
    } catch (err) {
      log.error("Error fetching conversation:", err);
    }
  }, [id, token]);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await messageAction.getConversion(conversationId);
      setMessages(res || {});
    } catch (error) {
      log.error("Error fetching messages:", error);
    }
  }, [conversationId]);

  const messageSeenUpdate = useCallback(async () => {
    if (!conversationId) return;
    try {
      await messageAction.messageSeen(conversationId);
    } catch (error) {
      log.error("Error fetching messages:", error);
    }
  }, [conversationId]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    const container = chatContainerRef.current;
    if (container) {
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
      fetchConversation();
    }
  }, [id, fetchUserDetails, fetchConversation]);

  useEffect(() => {
    loadMessages();
  }, [conversationId, loadMessages]);

  // Scroll to bottom after messages are loaded
  useEffect(() => {
    if (conversationId && Object.keys(messages).length > 0) {
      // Scroll after messages are loaded
      setTimeout(() => {
        scrollToBottom();
      }, 500);
    }
  }, [conversationId, messages, scrollToBottom]);

  // Scroll to bottom when conversation changes or component mounts
  useEffect(() => {
    if (conversationId) {
      // Add delay to ensure conversation is loaded
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    }
  }, [conversationId, scrollToBottom]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (Object.keys(messages).length > 0) {
      // Add a small delay to ensure messages are rendered
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
  }, [messages, scrollToBottom]);

  // Scroll on resize (mobile keyboard) - additional safeguard
  useEffect(() => {
    const handleResize = () => {
        if (chatContainerRef.current) {
            scrollToBottom();
        }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scrollToBottom]);

  // Join & leave room
  useEffect(() => {
    if (socket && conversationId) {
      socket?.emit("join-room", conversationId);
      messageSeenUpdate();
      return () => {
        socket?.emit("leave-room", conversationId);
      };
    }
  }, [socket, conversationId, messageSeenUpdate]);

  // Socket message listener
  useEffect(() => {
    if (!socket || !conversationId) return;

    const handleMessage = (msg) => {
      console.log("handleMessage msg:", msg);
      console.log("handleMessage userId:", userId);
      console.log("handleMessage conversationId:", conversationId);
      if (parseInt(msg.roomId) !== parseInt(conversationId)) return;
      if (parseInt(msg.recepientId) === parseInt(userId)) {
        console.log("handleMessage enter successfully");
        setMessages((prev) => ({
          ...prev,
          [dayjs().format("YYYY-MM-DD")]: [
            ...(prev[dayjs().format("YYYY-MM-DD")] ?? []),
            {
              sender: "other",
              message: msg.message,
              time: dayjs().format("h:mm A"),
            },
          ],
        }));
        // Scroll to bottom when receiving new message
        scrollToBottom();
      }
    };

    socket.on("receive-message", handleMessage);

    return () => {
      socket.off("receive-message", handleMessage);
    };
  }, [socket, conversationId, userId, scrollToBottom]);

  // Handle reconnect
  useEffect(() => {
    if (!socket || !conversationId) return;

    const handleReconnect = () => {
      socket.emit("join-room", conversationId);
    };

    socket.on("connect", handleReconnect);
    return () => {
      socket.off("connect", handleReconnect);
    };
  }, [socket, conversationId]);

  // Handle socket errors
  useEffect(() => {
    if (!socket) return;

    const handleError = (err) => log.error("ChatBox Socket error:", err);
    const handleDisconnect = () => log.warn("ChatBox Socket disconnected");

    socket.on("error", handleError);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off("error", handleError);
      socket.off("disconnect", handleDisconnect);
    };
  }, [socket]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !conversationId || isSending) return;

    setIsSending(true);

    try {
      if (location?.state?.body?.type === "request") {
        await messageAction.chatRequest({
          participantId: id,
          message: text,
          conversationId: conversationId,
        });
      } else {
        const sendMessage = {
          message: text,
          roomId: conversationId,
          sentAt: Date.now(),
          type: 1,
          recepientId: id,
        };

        const result = await messageAction.sendMessage(sendMessage);
        console.log("result:", result);

        if (result?.status) {
          setMessages({
            ...messages,
            [dayjs().format("YYYY-MM-DD")]: [
              ...(messages[dayjs().format("YYYY-MM-DD")]
                ? messages[dayjs().format("YYYY-MM-DD")]
                : []),
              {
                sender: "you",
                message: text,
                time: dayjs().format("h:mm A"),
              },
            ],
          });
          // Scroll to bottom after sending message
          scrollToBottom();
        } else {
          console.error("Failed to send message:", result.error);
          // You might want to show an error message to the user here
        }
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsSending(false);
      setText("");
    }
  };

  const handleReportSuccess = useCallback(() => {
    fetchUserDetails();
    // Optimistically set reportedByMe to true (no local storage)
    setChatAction(prev => ({ ...prev, isReported: true, reportedByMe: true }));
    setShowReportModal(false);
  }, [fetchUserDetails]);

  const handleUnblock = async () => {
    try {
      setLoader(true);
      await unblockUser(id);
      
      // Refresh user status
      const userStatus = await profileAction.getUserActionStatus(id);
      if (userStatus.status) {
        setChatAction({
          isMutualMatch: userStatus.data.isMutualMatch,
          isUnmatched: userStatus.data.isUnmatched,
          isBlocked: userStatus.data.isBlocked,
          blockedByMe: false, // if we just unblocked, it's definitely false
          isReported: userStatus.data.isReported,
          // If we unblock, reported status shouldn't change, but let's preserve it or re-fetch
          // reportedByMe: chatAction.reportedByMe // Might need to re-verify if strict
        });
        // Re-fetch details to be sure about reported status if needed
        fetchUserDetails();
      }
    } catch (error) {
      log.error("Error unblocking user:", error);
    } finally {
      setLoader(false);
    }
  };

  const renderDropdown = () => {
    const { approval } = participantProfile;
    const { isUnmatched, isBlocked, isReported, isMutualMatch, blockedByMe } = chatAction;

    if (approval !== UserApprovalEnum.APPROVED) {
      return null;
    }

    const isActionDisabled = isUnmatched || (isBlocked && !blockedByMe); // Hide if blocked by other, or unmatched. Allow operations if reported.
    if (isActionDisabled) {
      return null; 
    }

    return (
      <Dropdown>
        <Dropdown.Toggle
          as="div"
          style={{ cursor: "pointer" }}
          id="dropdown-basic"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path
              d="M17.33 6.67A1.33 1.33 0 1 1 14.66 6.67 1.33 1.33 0 0 1 17.33 6.67Z"
              stroke="#3A3A3A"
              strokeWidth="2"
            />
            <path
              d="M17.33 16a1.33 1.33 0 1 1-2.67 0 1.33 1.33 0 0 1 2.67 0Z"
              stroke="#3A3A3A"
              strokeWidth="2"
            />
            <path
              d="M17.33 25.33A1.33 1.33 0 1 1 14.66 25.33 1.33 1.33 0 0 1 17.33 25.33Z"
              stroke="#3A3A3A"
              strokeWidth="2"
            />
          </svg>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {isMutualMatch && !isUnmatched && !isBlocked && (
            <Dropdown.Item
              onClick={() => setShowUnmatchModal(true)}
              style={{
                borderBottom: "1px solid #e0e0e0",
                marginBottom: "0.5rem",
              }}
            >
              Unmatch
            </Dropdown.Item>
          )}
          {!isReported && !isBlocked && (
            <Dropdown.Item
              onClick={() => setShowReportModal(true)}
              style={{
                borderBottom: "1px solid #e0e0e0",
                marginBottom: "0.5rem",
              }}
            >
              Report
            </Dropdown.Item>
          )}
          {!isBlocked ? (
            <Dropdown.Item onClick={() => setShowBlockModal(true)}>
              Block
            </Dropdown.Item>
          ) : blockedByMe ? (
            <Dropdown.Item onClick={handleUnblock}>
              Unblock
            </Dropdown.Item>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderSendMessageInput = () => {
    const { approval, active } = participantProfile;
    const { isUnmatched, isBlocked, isReported } = chatAction;

    if (approval !== UserApprovalEnum.APPROVED) {
      return (
        <div className={styles.chatStatusContainer}>
          <p className={styles.chatStatusMessage}>User not approved</p>
        </div>
      );
    }

    // if (isUnmatched) {
    //   return (
    //     <div className={styles.chatStatusContainer}>
    //       <p className={styles.chatStatusMessage}>Unmatched user</p>
    //     </div>
    //   );
    // }
    // if (isBlocked) {
    //   return (
    //     <div className={styles.chatStatusContainer}>
    //       <p className={styles.chatStatusMessage}>Blocked user</p>
    //     </div>
    //   );
    // }
    // if (isReported) {
    //   return (
    //     <div className={styles.chatStatusContainer}>
    //       <p className={styles.chatStatusMessage}>Reported user</p>
    //     </div>
    //   );
    // }

    const canSendMessage =
      approval === UserApprovalEnum.APPROVED &&
      active === 1 &&
      !isUnmatched &&
      !isUnmatched &&
      !isBlocked;
      // !isReported; // Allow chat even if reported

    if (!canSendMessage) return null;



    return (
      <div style={{ 
        position: 'sticky', 
        bottom: 0, 
        background: '#f9fafb', // Match background to avoid transparency issues
        paddingBottom: 'env(safe-area-inset-bottom)',
        zIndex: 20
      }}>

          {!chatAction.isBlocked && chatAction.reportedByMe && isReportBannerVisible && (
             <div className={styles.chatStatusContainer} style={{ padding: '0.5rem 1.5rem 0' }}>
               <p className={styles.reportBanner}>
                 Report submitted. You can block this person anytime.
                 <button 
                   className="ms-2 btn btn-link text-danger p-0 align-baseline" 
                   style={{ textDecoration: 'underline', fontSize: 'inherit', whiteSpace: 'nowrap', display: 'inline' }}
                   onClick={() => setShowBlockModal(true)}
                 >
                   Block
                 </button>
                 <IoClose 
                   className="ms-2 align-middle" 
                   style={{ cursor: 'pointer', color: '#6b7280', fontSize: '1.2rem', minWidth: '20px' }}
                   onClick={() => setIsReportBannerVisible(false)}
                 />
               </p>
             </div>
          )}
          <form className={styles.chatInput} style={{ margin: '1rem 1.5rem 1.5rem' }} onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder="Message..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              onFocus={() => {
                // Delay to allow keyboard to show
                setTimeout(scrollToBottom, 300);
                setTimeout(scrollToBottom, 600);
              }}
            />
            <button type="submit" disabled={!text.trim() || isSending}>
              {isSending ? (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  />
              ) : (
                "Send"
              )}
            </button>
          </form>
      </div>
    );

  };

  if (loader) return <Loader />;

  if (!id || !conversationId || participantProfile.deletedAt) {
    return (
      <>
        {!mobile && (
          <div className={styles.chatPlaceholder}>
            <p>Meet your match, say hello and start something wonderful!</p>
          </div>
        )}
      </>
    );
  }

  return (
    <section 
      ref={chatSectionRef}
      className={[styles.chatbox, className].join(" ")}
      style={{
        height: '100dvh', // Default fallback
        position: 'relative' // Needed for reliable absolute/handling
      }}
    >
      <UnmatchModal
        personId={id}
        show={showUnmatchModal}
        setShow={setShowUnmatchModal}
      />
      <ReportModal
        personId={id}
        show={showReportModal}
        setShow={setShowReportModal}
        onReportSuccess={handleReportSuccess}
      />
      <BlockModal
        personId={id}
        show={showBlockModal}
        setShow={setShowBlockModal}
      />

      <div className={styles.chatHeader}>
        <IoArrowBack size={24} cursor="pointer" onClick={() => navigate(-1)} />
        <img
          src={participantProfile.image}
          alt="profile"
          onClick={() =>
            participantProfile.approval !== 30
              ? navigate(
                  `/user/${resolveProfileId({
                    user_id: id,
                  })}?token=${encodeURIComponent(
                    participantProfile.profile_token || ""
                  )}`,
                  {
                    state: {
                      profile_token: participantProfile.profile_token,
                    },
                  }
                )
              : navigate(`/user/home`)
          }
        />
        <p>{participantProfile.name}</p>
        <div style={{ flexGrow: "1" }}></div>
        {renderDropdown()}
      </div>

      <div id="chat-container" className={styles.chatContainer} ref={chatContainerRef}>
        <div className={styles.chatContainerBox}>
          {Object.entries(messages || {})?.map(([date, msgs]) => (
            <React.Fragment key={date}>
              <div className={styles?.date}>
                <hr />
                <p>{formatDate(date)}</p>
                <hr />
              </div>
              {Array.isArray(msgs) &&
                msgs?.map((msg, idx) => (
                  <div key={idx} className={styles.message}>
                    <div
                      className={
                        msg.sender === "you" ? styles.you : styles.other
                      }
                    >
                      <p style={{ fontSize: "16px" }}>{msg.message}</p>
                      <p>
                        {dayjs(msg.sent_at)
                          .tz(dayjs.tz.guess())
                          .format("h:mm A")}
                      </p>
                    </div>
                  </div>
                ))}
            </React.Fragment>
          ))}
          {/* Dummy element for scroll anchoring if needed, though scrollTop logic works fine usually */}
          <div style={{ float:"left", clear: "both" }}></div>
        </div>
      </div>
      
      {chatAction.isBlocked && (
         <div className={styles.chatStatusContainer}>
           {chatAction.blockedByMe ? (
             <p className={styles.chatStatusMessage}>
               You have blocked this user
               <button 
                 className="ms-3 btn btn-link text-danger p-0 align-baseline" 
                 style={{ textDecoration: 'underline', fontSize: 'inherit' }}
                 onClick={handleUnblock}
               >
                 Unblock
               </button>
             </p>
           ) : (
             <p className={styles.chatStatusMessage}>
               This conversation is no longer available.
             </p>
           )}
         </div>
      )}


      {renderSendMessageInput()}
    </section>
  );
}
